import CloseIcon from '@mui/icons-material/Close';
import { Popover, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import {
  darkTeal,
  fadedTealBackground,
  teal2,
} from '../../../common/theme/color-constants';
import { Languages } from '../../../common/v2/constants';
import { useGlobalLocaleContext } from '../../../common/v2/context/contexts';

const StyledPopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${darkTeal};
  color: white;
  padding: 8px 12px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const StyledPopoverTitle = styled(Typography)`
  font-weight: 600;
`;

const StyledCloseButton = styled(CloseIcon)`
  cursor: pointer;
  font-size: 1.25rem;
  &:hover {
    opacity: 0.8;
  }
`;

const StyledPopoverContent = styled.div`
  padding: 12px;
  font-size: 0.875rem;
  box-sizing: border-box;
  overflow-y: auto;
  flex-grow: 1;

  dl {
    margin: 0;
    padding: 0;
  }

  dt {
    font-weight: bold;
    color: white;
    background-color: ${teal2};
    padding: 6px 10px;
    margin-top: 12px;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    display: block;
    position: relative;
    z-index: 1;
  }

  dl > dt:first-of-type {
    margin-top: 0;
  }

  dd {
    background-color: ${fadedTealBackground};
    color: #333;
    margin-left: 0;
    margin-top: 0;
    padding: 8px 10px 10px 24px;
    border-radius: 0 0 4px 4px;
    display: block;
    position: relative;
  }
`;

export interface PopoverData {
  metricCategoryName: string;
  metricGroupName: string;
  metricName: string;
  tooltipJapanese: string | null;
  tooltipEnglish: string | null;
}

interface MetricTooltipProps {
  open: boolean;
  anchorEl: Element | null;
  onClose: () => void;
  popoverData: PopoverData | null;
}

export const MetricTooltip: React.FC<MetricTooltipProps> = ({
  open,
  anchorEl,
  onClose,
  popoverData,
}) => {
  const { selected: selectedLanguage } = useGlobalLocaleContext();
  const { t } = useTranslation();

  if (!popoverData) {
    return null;
  }

  const htmlTooltipContent =
    match(selectedLanguage)
      .with(Languages.JA, () => popoverData.tooltipJapanese)
      .with(Languages.EN, () => popoverData.tooltipEnglish)
      .exhaustive() ?? '';

  const sanitizedHtmlTooltip = DOMPurify.sanitize(htmlTooltipContent);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        style: {
          borderRadius: '4px',
          boxSizing: 'border-box',
        },
      }}
      sx={{
        [`& .MuiPopover-paper`]: {
          maxHeight: '80vh',
          minWidth: '300px',
          maxWidth: '500px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <StyledPopoverHeader>
        <StyledPopoverTitle>
          {t('pages:metrics.metricTooltip.headerTitle')}
        </StyledPopoverTitle>
        <StyledCloseButton
          onClick={onClose}
          data-cy="metric-info-popover-close-button"
        />
      </StyledPopoverHeader>
      <StyledPopoverContent>
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ wordBreak: 'break-word' }}
        >
          <strong>{t('pages:metrics.metricTooltip.metricLabel')}</strong>
          <span style={{ marginLeft: '0.5em' }}>
            {popoverData.metricCategoryName}
          </span>
          <span style={{ margin: '0 0.4em' }}>›</span>
          {popoverData.metricGroupName}
          <span style={{ margin: '0 0.4em' }}>›</span>
          <strong>{popoverData.metricName}</strong>
        </Typography>

        {/* The div for dangerouslySetInnerHTML will only be rendered if sanitizedHtmlTooltip has content due to the check above */}
        <Typography
          variant="subtitle2"
          component="div"
          gutterBottom
          sx={{ marginTop: '16px', fontWeight: 'bold' }}
        >
          {t('pages:metrics.metricTooltip.explanationLabel')}
        </Typography>
        <div dangerouslySetInnerHTML={{ __html: sanitizedHtmlTooltip }} />
      </StyledPopoverContent>
    </Popover>
  );
};

export default MetricTooltip;
